'use client';

/**
 * Affiliate "Wear with AI" badge — gradient box + the multi-path WearWithAi
 * glyph, ported from RN. The wear-with-ai *action* is deferred; this is the
 * visual affordance only (RN renders it on eligible affiliate products).
 *
 * Lives in `@ps/ui` (not a feature slice) so both the shared `ProductCard`
 * molecule and the `@ps/features` product surfaces render the SAME badge.
 */
export function WearWithAiBadge({ size = 22 }: { size?: number }) {
  const glyph = size - 8;
  return (
    <span
      className="border-content-2 flex items-center justify-center border"
      style={{
        width: size,
        height: size,
        borderRadius: size / 4,
        background:
          'linear-gradient(to bottom, rgb(var(--sink)), hsl(var(--brand)))',
      }}
    >
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden
      >
        <path
          d="M6.83301 7.46781C6.66786 7.87849 6.36271 8.67359 6.04717 9.68195C5.66546 10.903 5.26789 12.438 5.0847 13.985C4.40987 14.2207 3.93519 14.1748 3.60379 14.0184C3.29591 13.8729 3.10834 13.6307 2.99951 13.4349C3.92918 10.6761 5.49156 8.16612 5.95694 7.44922L6.83301 7.46781Z"
          fill="#fff"
        />
        <path
          d="M8.43945 7.46853C8.60461 7.87976 8.9103 8.67595 9.22584 9.68541C9.60755 10.9054 10.004 12.4382 10.1872 13.983C10.9031 14.2345 11.3941 14.1695 11.7277 13.9917C12.0378 13.8266 12.2166 13.5625 12.3129 13.3678C11.3799 10.6331 9.83997 8.15864 9.37841 7.44775C9.06725 7.44939 8.75171 7.45978 8.43945 7.46853Z"
          fill="#fff"
        />
        <path
          d="M8.17938 7.14252C8.18649 7.14197 8.1936 7.14197 8.20071 7.14197L9.21623 7.1201L9.3174 6.30859L7.78345 6.34195V6.3425H7.77963H7.77908H7.55487C7.54885 6.3425 7.54283 6.34195 7.53682 6.3414L6.01709 6.30859C6.051 6.57928 6.0849 6.84996 6.11881 7.1201L7.07199 7.14088C7.0791 7.14033 7.08621 7.14088 7.09331 7.14143L7.54448 7.15127C7.54776 7.15127 7.55158 7.15072 7.55541 7.15072H7.78072V7.15127L8.17938 7.14252Z"
          fill="#fff"
        />
        <path
          d="M9.85273 13.9879C9.67062 12.4787 9.28235 10.9798 8.9083 9.78384C8.56268 8.67923 8.23019 7.83437 8.08363 7.47674L7.78395 7.4833V7.48385H7.78012H7.77957H7.55536C7.54934 7.48385 7.54333 7.4833 7.53731 7.48275L7.18951 7.4751C7.0435 7.83164 6.711 8.6765 6.36539 9.78056C5.99133 10.9765 5.60252 12.4754 5.42041 13.9852C5.83548 14.1131 7.15342 14.5052 7.65489 14.4998C8.19792 14.4937 9.44531 14.1159 9.85273 13.9879Z"
          fill="#fff"
        />
        <path
          d="M9.38873 5.97405L7.78096 6.00905V6.00851H7.55565C7.55183 6.00851 7.54854 6.00851 7.54472 6.00905L5.94733 5.9746H5.94678C5.16313 5.07123 5.9014 3.70632 5.45898 2.55523C5.50273 2.52789 5.54758 2.51094 5.58476 2.5C5.72038 2.6821 5.96264 2.89864 6.23334 3.11191C6.83762 3.58656 7.64042 4.05848 7.66776 4.05848C7.72081 4.05848 8.4793 3.60734 9.06991 3.14308C9.35374 2.91997 9.60967 2.69085 9.75076 2.5C9.78794 2.51094 9.83224 2.52789 9.87654 2.55468C9.43741 3.69867 10.1675 5.06576 9.39584 5.96585C9.39366 5.96859 9.39092 5.97132 9.38873 5.97405Z"
          fill="#fff"
        />
        <path
          d="M11.0303 3.03629C10.9896 3.02854 10.9891 2.97413 11.0296 2.96565L12.1092 2.74052C12.4978 2.65949 12.8 2.23785 12.8502 1.70693L12.961 0.532881C12.9651 0.48904 13.0335 0.48904 13.0376 0.532881L13.1484 1.70693C13.1985 2.23785 13.5008 2.65949 13.8894 2.74052L14.9694 2.96565C15.0099 2.97413 15.0094 3.02854 14.9687 3.03629L13.9005 3.24C13.5064 3.3151 13.1982 3.74039 13.1482 4.27787L13.0376 5.46705C13.0335 5.51098 12.965 5.51098 12.961 5.46705L12.8504 4.27787C12.8004 3.74039 12.4921 3.31519 12.0981 3.24L11.0303 3.03629Z"
          fill="#fff"
        />
        <path
          d="M2.01519 1.72822C1.99467 1.72443 1.99438 1.69812 2.01485 1.694L2.55935 1.58498C2.75537 1.54572 2.90778 1.34156 2.93306 1.08438L2.98894 0.515741C2.991 0.494477 3.02551 0.494477 3.02757 0.515741L3.08345 1.08438C3.10873 1.3415 3.26119 1.54572 3.45716 1.58498L4.00189 1.694C4.02235 1.69807 4.02207 1.72443 4.00155 1.72822L3.46277 1.82686C3.264 1.86325 3.10856 2.06919 3.08334 2.32952L3.02751 2.90544C3.02545 2.9267 2.99095 2.9267 2.98888 2.90544L2.93306 2.32946C2.90784 2.06913 2.75239 1.8632 2.55362 1.8268L2.01519 1.72822Z"
          fill="#fff"
        />
      </svg>
    </span>
  );
}
